import React, { useState, useEffect, useMemo, useCallback } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { horoscopeDetailFetchData } from "../../../redux/actions/horoscopeDetailAction";
import { sharecount } from "../../../redux/actions/commonAction";
import ReactGA from "react-ga4";
import InjectScript from "../../CustomScript";
import { AllViewCountActionFetchData } from "../../../redux/actions/AllViewCountAction";

const AstrologyDetailpage = () => {
  const { rashi } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [showModal, setShowModal] = useState(false);
  const [hoveredRashi, setHoveredRashi] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const scrollContainerRef = React.useRef(null);

  useEffect(() => {
    setIsExpanded(false);
  }, [rashi, location.pathname]);

  const scrollTabs = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 150;
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Default to 'daily' if no period is specified
  // Determine active period based on URL path
  const activePeriod = useMemo(() => {
    if (location.pathname.endsWith("/weekly")) return "weekly";
    if (location.pathname.endsWith("/yearly")) return "yearly";
    if (location.pathname.endsWith("/tarot")) return "tarot";
    return "daily";
  }, [location.pathname]);

  // Accessing the Redux state
  const { data: horoscopeDetailState, loading: horoscopeLoading, error: horoscopeError } = useSelector(
    (state) => state?.HoroscopeDetailReducer || {}
  );
  const { 21: assetbaseUrl } = useSelector(
    (state) => state?.SettingReducer?.data || {}
  );
  const rawHoroscopeData = useSelector((state) => state?.HoroscopeReducer?.data);
  const Horoscope = useMemo(() => {
    if (!rawHoroscopeData) return [];
    if (Array.isArray(rawHoroscopeData)) return rawHoroscopeData;

    if (location.pathname.includes("/tarot")) {
      return rawHoroscopeData.Tarot || [];
    } else if (/^\d+$/.test(rashi)) {
      return rawHoroscopeData.Numerology || [];
    }
    return rawHoroscopeData.Astrology || [];
  }, [rawHoroscopeData, location.pathname, rashi]);

  const horoscopeList = Horoscope;

  // Select the appropriate data based on the active period
  const selectedData = useMemo(() => {
    if (horoscopeDetailState) {
      let data = null;
      // If the API returns an object with periods as keys (daily, weekly, yearly)
      if (
        typeof horoscopeDetailState === "object" &&
        !Array.isArray(horoscopeDetailState)
      ) {
        // Map 'tarot' or 'daily' URL segment to 'daily' key in API response
        const dataKey = (activePeriod === "tarot" || !activePeriod) ? "daily" : activePeriod;
        data = horoscopeDetailState[dataKey];
      }
      // If the API returns an array (backward compatibility)
      else if (Array.isArray(horoscopeDetailState)) {
        const dataKey = (activePeriod === "tarot" || !activePeriod) ? "daily" : activePeriod;
        data =
          horoscopeDetailState.find((item) => item.type === dataKey) ||
          horoscopeDetailState[0];
      }

      // Ensure the data belongs to the currently requested rashi
      // If data is present but doesn't have url/name (common in some specific API responses), 
      // we trust the API result since it was fetched by rashi slug.
      if (data) {
        const matchesRashi = !rashi ||
          data.url?.toLowerCase() === rashi.toLowerCase() ||
          data.name?.toLowerCase() === rashi.toLowerCase();

        if (matchesRashi || (!data.url && !data.name)) {
          return data;
        }
      }
    }
    return null;
  }, [horoscopeDetailState, activePeriod, rashi]);

  const formattedDate = useMemo(() => {
    const displayDate = selectedData?.date_range || selectedData?.date;
    if (!displayDate) return "";

    // If it's just a year (e.g., "2025")
    if (/^\d{4}$/.test(displayDate)) {
      return displayDate;
    }

    try {
      const dateObj = new Date(displayDate);
      if (isNaN(dateObj.getTime())) return displayDate; // Return as is if invalid date      

      return dateObj.toLocaleDateString("gu-IN", {
        weekday: activePeriod === "daily" ? "long" : undefined,
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (e) {
      return displayDate;
    }
  }, [selectedData, activePeriod]);

  const currentRashiInfo = useMemo(() => {
    if (Horoscope && rashi) {

      return Horoscope?.find(
        (h) =>
          h.url?.toLowerCase() === rashi?.toLowerCase() ||
          h.name?.toLowerCase() === rashi?.toLowerCase()
      );
    }
    return null;
  }, [Horoscope, rashi]);

  const { name, name_guj, detail, media, letters, h1, seo_desc, seo_keywords } =
    selectedData || currentRashiInfo || {};

  const displayH1 = useMemo(() => {
    return h1 || (name_guj ? `${name_guj} રાશિફળ` : "રાશિફળ");
  }, [h1, name_guj]);

  const { type, fetchCategory } = useMemo(() => {
    let type = "rashifal";
    if (location.pathname.includes("/tarot")) {
      type = "tarot";
    } else if (/^\d+$/.test(rashi)) {
      type = "numerology";
    }
    const fetchCategory = type === "rashifal" ? "" : type;
    return { type, fetchCategory };
  }, [location.pathname, rashi]);

  const isNumerology = type === "numerology";
  const isTarot = type === "tarot";

  // Numerology Birth Date Mapping
  const getBirthDates = useCallback((num) => {
    const mapping = {
      1: "1, 10, 19, 28",
      2: "2, 11, 20, 29",
      3: "3, 12, 21, 30",
      4: "4, 13, 22, 31",
      5: "5, 14, 23",
      6: "6, 15, 24",
      7: "7, 16, 25",
      8: "8, 17, 26",
      9: "9, 18, 27",
    };
    return mapping[num] || "";
  }, []);

  // Determine which tabs to show based on available data
  const availableTabs = useMemo(() => {
    const allTabs = [
      { id: "daily", label: "આજનું રાશિફળ" },
      { id: "weekly", label: "સાપ્તાહિક રાશિફળ" },
      { id: "yearly", label: "વાર્ષિક રાશિફળ" },
    ];

    if (horoscopeDetailState && typeof horoscopeDetailState === "object" && !Array.isArray(horoscopeDetailState)) {
      return allTabs.filter((tab) => horoscopeDetailState[tab.id] !== null);
    }
    return allTabs;
  }, [horoscopeDetailState]);

  const isTabActive = useCallback((tabId) => {
    if (activePeriod === "tarot" || activePeriod === "daily") {
      return tabId === "daily";
    }
    return activePeriod === tabId;
  }, [activePeriod]);

  useEffect(() => {
    ReactGA.event({
      category: "Astrology Detail Page",
      action: "Astrology Detail Page",
      label: displayH1,
    });

    if (rashi) {
      dispatch(horoscopeDetailFetchData(rashi, fetchCategory));
    }
  }, [dispatch, rashi, fetchCategory, displayH1]);

  const getRashiImage = (rashiName) => {
    if (horoscopeList && horoscopeList.length > 0) {
      const found = horoscopeList.find(
        (h) =>
          h.name.toLowerCase() === rashiName.toLowerCase() ||
          h.name_guj === rashiName
      );
      if (found && found.media) {
        return `${assetbaseUrl?.value}${found.media}`;
      }
    }
    return null;
  };

  const handleRashiClick = (rashiName) => {
    setShowModal(false);
    navigate(`/astrology/${rashiName}/${activePeriod}`);
  };

  useEffect(() => {
    dispatch(AllViewCountActionFetchData(window.location.pathname));
  }, [window.location.pathname]); // dependency on pathname

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showModal]);

  if (horoscopeLoading && !selectedData) {
    return (
      <div
        className="container mt-5 text-center"
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className="spinner-border text-danger" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  const returnStop4 = (e) => {
    ReactGA.event({
      category: "Share",
      action: "Email",
      label: urlshare,
    });
    e.stopPropagation();
    return false;
  };
  // Handle share action
  const handleShare = async (name) => {
    // document.getElementById(id).classList.add("d-none");
    await dispatch(sharecount(name));
    return false;
  };
  // Handle Facebook share
  const handleFacebookShare = (e) => {
    e.stopPropagation();
    handleShare("fb");
    ReactGA.event({ category: "Share", action: "Facebook", label: urlshare });
    return false;
  };

  const urlshare = `\n\n${window.location.href}`;
  const mediaa = assetbaseUrl?.value + media + "?resize=1200,630";
  const title = `આજનું ${name_guj} રાશિફળ`;

  const shareTitle = `આજનું ${name_guj} રાશિફળ`;
  // const shareTitle = innerData?.title || "";
  const shareSource = "via @sandeshnews";
  const shareUrl = `\n\n${window.location.href}`;
  const appMsg =
    "\n\nDownload the Sandesh app now to read our latest stories\nhttps://sandesh.com/d";

  const waShareText = `${shareTitle} ${shareSource} ${shareUrl} ${appMsg}`;

  // // Function to handle share events and dispatch share count
  // const returnfalse = useCallback(
  //   async (name) => {
  //     // await dispatch(sharecount(name, props?.dataid));
  //     await dispatch(sharecount(name));
  //   },
  //   [dispatch]
  // );
  const returnfalse = async (name) => {
    await dispatch(sharecount(name));
  };

  const adtwoHardcode166 =
    "" +
    "<div id='div-gpt-ad-1646309696587-0' class='ad-placeholder' style='min-width: 300px; min-height: 250px;'>  " +
    "<script>" +
    "window.googletag = window.googletag || {cmd: []};" +
    "googletag.cmd.push(function() { " +
    "googletag.defineSlot('/22678428348/Mweb_HP_Desktop_ATF_300X250', [300, 250], 'div-gpt-ad-1646309696587-0').addService(googletag.pubads()); " +
    "googletag.pubads().enableSingleRequest();" +
    "googletag.pubads().disableInitialLoad(); " +
    "googletag.enableServices();  " +
    "});" +
    "</script>" +
    "" +
    "<script>" +
    "googletag.cmd.push(function() { googletag.display('div-gpt-ad-1646309696587-0'); });" +
    "</script>" +
    "</div>" +
    // '' +
    // '<script async="async" src="https://cdn.unibots.in/headerbidding/adScriptSandesh_new.js">' +
    // ' </script>' +
    // '' +
    "" +
    "";

  const adtwoHardcode4 =
    "" +
    "<div id='div-gpt-ad-1645339970921-0' class='ad-placeholder' style='min-width: 300px; min-height: 250px;'>  " +
    "<script>" +
    " window.googletag = window.googletag || {cmd: []}; " +
    "googletag.cmd.push(function() { " +
    "googletag.defineSlot('/22678428348/SDPL_HP_Desktop_ATF_300X250', [300, 250], 'div-gpt-ad-1645339970921-0').addService(googletag.pubads());  " +
    "googletag.pubads().enableSingleRequest(); " +
    "googletag.pubads().disableInitialLoad();" +
    " googletag.enableServices(); " +
    "});" +
    "</script>" +
    "" +
    "<script>" +
    " googletag.cmd.push(function() { googletag.display('div-gpt-ad-1645339970921-0'); });   " +
    "</script>" +
    "</div>" +
    // '' +
    // '<script async="async" src="https://cdn.unibots.in/headerbidding/adScriptSandesh_new.js">' +
    // ' </script>' +
    // '' +
    "" +
    "";

  return (
    <>
      <div>
        {/* Modal Overlay with consistent styling */}
        {showModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              zIndex: 99999999,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              backdropFilter: "blur(5px)",
              WebkitBackdropFilter: "blur(5px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "fadeIn 0.2s ease-out",
            }}
            onClick={() => setShowModal(false)}
          >
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "20px",
                padding: "20px 25px", // Reduced vertical padding
                maxWidth: "600px",
                width: "95%",
                // removed fixed height/overflow to fit content naturally
                position: "relative",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                border: "1px solid rgba(255,255,255,0.5)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                {" "}
                {/* Reduced margin */}
                <h3
                  style={{
                    color: "#2c3e50",
                    fontWeight: "800",
                    fontSize: "18px", // Smaller font
                    margin: 0,
                    letterSpacing: "-0.5px",
                  }}
                >
                  તમારી રાશિ અહી પસંદ કરો
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    background: "transparent",
                    border: "none",
                    borderRadius: "50%",
                    width: "28px",
                    height: "28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "18px",
                    color: "#95a5a6",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "#e74c3c";
                    e.currentTarget.style.background = "#fdedec";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#95a5a6";
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  &times;
                </button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    window.innerWidth < 480 ? "repeat(3, 1fr)" : "repeat(4, 1fr)",
                  gap: "10px",
                }}
              >
                {Horoscope?.map((r, index) => {
                  const imgSrc = getRashiImage(r.name);
                  const isActive =
                    rashi &&
                    (rashi.toLowerCase() === r.url.toLowerCase() ||
                      rashi.toLowerCase() === r.name.toLowerCase());
                  const isHovered = hoveredRashi === r.name;

                  return (
                    <div
                      key={r.name}
                      onClick={() => handleRashiClick(r.url)}
                      onMouseEnter={() => setHoveredRashi(r.name)}
                      onMouseLeave={() => setHoveredRashi(null)}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "8px",
                        borderRadius: "12px",
                        cursor: "pointer",
                        backgroundColor: isActive
                          ? "#fff5f5"
                          : isHovered
                            ? "#f8f9fa"
                            : "transparent",
                        border: isActive
                          ? "1px solid #ffcdd2"
                          : isHovered
                            ? "1px solid #e0e0e0"
                            : "1px solid transparent",
                        transition: "all 0.2s ease",
                        transform: isHovered ? "scale(1.02)" : "scale(1)",
                      }}
                    >
                      <div
                        style={{
                          width: "35px",
                          height: "35px",
                          marginBottom: "5px",
                        }}
                      >
                        {imgSrc && (
                          <img
                            src={imgSrc}
                            alt={r.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        )}
                      </div>
                      <div
                        style={{
                          color: isActive ? "#c62828" : "#2c3e50",
                          fontWeight: "700",
                          fontSize: "13px",
                          lineHeight: "1.2",
                        }}
                      >
                        {r.name_guj}
                      </div>
                      <div
                        style={{
                          fontSize: "10px",
                          color: "#95a5a6",
                          fontWeight: "500",
                          marginTop: "2px",
                        }}
                      >
                        {r.letters ? r.letters : ""}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Main Page Container */}
        <div className="container mt-4 mb-5">
          <div className="row">
            {/* Left Sidebar Area */}
            {!isNumerology && (
              <div className="col-lg-3 d-none d-lg-block">
                <div className="sticky-top" style={{ top: "20px" }}>
                  <div
                    className="bg-white rounded mb-4"
                    style={{
                      boxShadow: "0 5px 20px rgba(0,0,0,0.05)",
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <div className="p-3">
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        {isNumerology ? (
                          /* Minimalist Sidebar for Numerology */
                          <div style={{ padding: "10px" }}>
                            <h4 style={{ fontSize: "14px", fontWeight: "800", color: "#ae0a0b", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "20px", textAlign: "center" }}>અન્ય અંક પસંદ કરો</h4>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => {
                                const isActive = String(rashi) === String(num);
                                return (
                                  <div
                                    key={num}
                                    onClick={() => navigate(`/astrology/${num}`)}
                                    style={{
                                      aspectRatio: "1/1",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      borderRadius: "50%",
                                      backgroundColor: isActive ? "#ae0a0b" : "#fff",
                                      color: isActive ? "#fff" : "#ae0a0b",
                                      cursor: "pointer",
                                      border: "1px solid #f0f0f0",
                                      fontWeight: "800",
                                      fontSize: "16px",
                                      boxShadow: isActive ? "0 5px 15px rgba(174,10,11,0.2)" : "none",
                                      transition: "all 0.3s ease"
                                    }}
                                  >
                                    {num}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          Horoscope?.map((r, index) => {
                            const imgSrc = getRashiImage(r.name);
                            const isActive =
                              rashi &&
                              (rashi.toLowerCase() === r.url.toLowerCase() ||
                                rashi.toLowerCase() === r.name.toLowerCase());

                            return (
                              <div
                                key={r.name}
                                onClick={() => handleRashiClick(r.url)}
                                style={{
                                  padding: "12px 16px",
                                  borderRadius: "12px",
                                  cursor: "pointer",
                                  backgroundColor: isActive
                                    ? "#fff5f5"
                                    : "transparent",
                                  color: isActive ? "#ae0a0b" : "#2c3e50",
                                  fontWeight: isActive ? "700" : "500",
                                  fontSize: "16px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "15px",
                                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                  borderLeft: isActive
                                    ? "4px solid #ae0a0b"
                                    : "4px solid transparent",
                                  position: "relative",
                                }}
                                onMouseEnter={(e) => {
                                  if (!isActive) {
                                    e.currentTarget.style.backgroundColor = "#fafafa";
                                    e.currentTarget.style.transform =
                                      "translateX(5px)";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isActive) {
                                    e.currentTarget.style.backgroundColor =
                                      "transparent";
                                    e.currentTarget.style.transform = "translateX(0)";
                                  }
                                }}
                              >
                                <div
                                  style={{
                                    width: isTarot ? "52px" : "52px",
                                    height: isTarot ? "75px" : "52px", // Taller for Tarot
                                    borderRadius: isTarot ? "4px" : "50%",
                                    boxShadow: isTarot ? "none" : "0 4px 12px rgba(0,0,0,0.08)",
                                    padding: isTarot ? "0" : "3px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: isTarot ? "transparent" : "#fff",
                                    transition: "transform 0.3s ease",
                                    transform: isActive ? "scale(1.05)" : "scale(1)",
                                    flexShrink: 0,
                                    overflow: "hidden"
                                  }}
                                >
                                  {imgSrc && (
                                    <img
                                      src={imgSrc}
                                      alt={r.name}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: isTarot ? "cover" : "contain",
                                        borderRadius: isTarot ? "4px" : "50%",
                                      }}
                                    />
                                  )}
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                  }}
                                >
                                  <span
                                    style={{
                                      lineHeight: "1.2",
                                      marginBottom: "2px",
                                      fontSize: "20px",
                                    }}
                                  >
                                    {r.name_guj}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "15px",
                                      color: isActive ? "#d32f2f" : "#95a5a6",
                                      fontWeight: "500",
                                      lineHeight: "1.2",
                                    }}
                                  >
                                    {r?.letters && `(${r?.letters})`}
                                  </span>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Main Content Area */}
            <div className={isNumerology ? "col-lg-9" : "col-lg-6"}>
              {/* Header Section */}
              <div className={`row d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 pb-3 border-bottom ${isNumerology ? "text-center" : ""}`}>
                <div className={`col-12 ${isNumerology ? "col-md-12" : "col-md-9"} mb-3 mb-md-0 ${isNumerology ? "text-center" : "text-start"}`}>
                  <h1
                    style={{
                      fontSize: window.innerWidth < 768 ? "22px" : "28px",
                      fontWeight: "900",
                      color: "#1a1a1a",
                      margin: 0,
                      lineHeight: "1.3",
                      letterSpacing: "-0.5px",
                    }}
                  >
                    <span style={{ color: "#ae0a0b" }}>{displayH1}</span>
                    {/* <span style={{ color: "#ae0a0b" }}>{આજનું ${name_guj} રાશિફળ``}</span> */}
                  </h1>
                  <p
                    className="text-muted mt-1 mb-0"
                    style={{ fontSize: "14px" }}
                  >
                    {formattedDate}
                    {window.location.pathname.includes("/weekly") &&
                      " સુધીનું રાશિફળ"}
                  </p>
                </div>
                {!isNumerology && (
                  <div className="col-12 col-md-3 d-flex justify-content-start justify-content-md-end">
                    <button
                      className="btn"
                      type="button"
                      onClick={() => setShowModal(true)}
                      style={{
                        borderRadius: "30px",
                        padding: "8px 25px",
                        fontSize: "15px",
                        fontWeight: "600",
                        backgroundColor: "#ae0a0b",
                        color: "white",
                        border: "none",
                        boxShadow: "0 4px 6px rgba(174, 10, 11, 0.2)",
                        transition: "transform 0.2s",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.transform = "translateY(-2px)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.transform = "translateY(0)")
                      }
                    >
                      <span>રાશિ પસંદ કરો</span>
                      <i
                        className="fas fa-chevron-down"
                        style={{ fontSize: "12px" }}
                      ></i>
                    </button>
                  </div>
                )}
              </div>

              {/* Tab Navigation with Arrows */}
              {!isNumerology && (
                <div className="mb-4 position-relative">
                  {/* Left Arrow - Only on mobile/tablet */}
                  {/* <button
                  onClick={() => scrollTabs("left")}
                  className="d-md-none"
                  style={{
                    position: "absolute",
                    left: "-5px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    background: "rgba(255,255,255,0.9)",
                    border: "none",
                    borderRadius: "50%",
                    width: "28px",~
                    height: "28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                    color: "#ae0a0b"
                  }}
                >
                  <i className="fas fa-chevron-left" style={{ fontSize: "12px" }}></i>
                </button> */}

                  <ul
                    className="nav nav-pills"
                    ref={scrollContainerRef}
                    style={{
                      gap: "10px",
                      background: "#f0efef4a",
                      // background: "#f2f2f2",
                      padding: "8px",
                      borderRadius: "15px",
                      display: "flex",
                      flexWrap: "nowrap",
                      overflowX: "auto",
                      WebkitOverflowScrolling: "touch",
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                      width: "max-content",
                      maxWidth: "100%",
                    }}
                  >
                    <style>{`
                    .nav-pills::-webkit-scrollbar { display: none; }
                  `}</style>
                    {availableTabs.map((tab) => (
                      <li className="nav-item" key={tab.id}>
                        <a
                          className={`nav-link ${isTabActive(tab.id) ? "active" : ""
                            }`}
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/astrology/${rashi}/${tab.id}`);
                          }}
                          onClickCapture={() =>
                            ReactGA.event({
                              category: "Astrology Detail Page",
                              action: "Astrology Detail Page",
                              label: displayH1,
                            })
                          }
                          style={{
                            backgroundColor:
                              activePeriod === tab.id ? "#fff" : "transparent",
                            color: activePeriod === tab.id ? "#ae0a0b" : "#666",
                            borderRadius: "12px",
                            padding: "8px 25px",
                            fontWeight: "700",
                            boxShadow:
                              activePeriod === tab.id
                                ? "0 5px 15px rgba(0,0,0,0.08)"
                                : "none",
                            whiteSpace: "nowrap",
                            transition: "all 0.3s ease",
                            fontSize: "15px",
                            border: "none",
                          }}
                        >
                          {tab.label}
                        </a>
                      </li>
                    ))}
                  </ul>

                  {/* Right Arrow - Only on mobile/tablet */}
                  {/* <button
                  onClick={() => scrollTabs("right")}
                  className="d-md-none"
                  style={{
                    position: "absolute",
                    right: "-5px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    background: "rgba(255,255,255,0.9)",
                    border: "none",
                    borderRadius: "50%",
                    width: "28px",
                    height: "28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                    color: "#ae0a0b"
                  }}
                >
                  <i className="fas fa-chevron-right" style={{ fontSize: "12px" }}></i>
                </button> */}
                </div>
              )}

              {/* Content Card */}
              {isNumerology && (
                <div
                  style={{
                    textAlign: "center",
                    marginBottom: "30px",
                  }}
                >
                  <h4 style={{ fontSize: "16px", fontWeight: "700", color: "#666", marginBottom: "20px" }}>તમારી જન્મ તારીખ મુજબ અંક પસંદ કરો</h4>
                  <div style={{ display: "flex", justifyContent: "center", gap: "10px", flexWrap: "wrap" }}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => {
                      const isCurrent = String(rashi) === String(num);
                      return (
                        <div
                          key={num}
                          onClick={() => navigate(`/astrology/${num}`)}
                          style={{
                            width: "45px",
                            height: "45px",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            fontSize: "18px",
                            fontWeight: "800",
                            border: isCurrent ? "none" : "1px solid #ae0a0b",
                            backgroundColor: isCurrent ? "#ae0a0b" : "#fff",
                            color: isCurrent ? "#fff" : "#ae0a0b",
                            boxShadow: isCurrent ? "0 5px 15px rgba(174,10,11,0.3)" : "none",
                            transition: "all 0.3s ease",
                          }}
                        >
                          {num}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div
                className="card border-0"
                style={{
                  borderRadius: "24px",
                  boxShadow: isNumerology
                    ? "0 30px 60px -12px rgba(0,0,0,0.1)"
                    : "0 15px 35px rgba(0,0,0,0.08)",
                  overflow: "hidden",
                  backgroundColor: "#fff",
                }}
              >
                {isNumerology ? (
                  /* Sleek Numerology Header Card */
                  <div style={{
                    padding: "50px 40px",
                    textAlign: "center",
                    borderBottom: "1px solid #f0f0f0"
                  }}>
                    <div style={{
                      fontSize: "42px",
                      fontWeight: "900",
                      color: "#1a1a1a",
                      marginBottom: "10px"
                    }}>અંક - {rashi}</div>

                    <div style={{
                      fontSize: "18px",
                      color: "#ae0a0b",
                      fontWeight: "700",
                      backgroundColor: "#fff5f5",
                      display: "inline-block",
                      padding: "8px 25px",
                      borderRadius: "50px",
                      marginBottom: "20px"
                    }}>
                      (જેમનો જન્મ {getBirthDates(rashi)} તારીખે થયો છે.)
                    </div>

                    <div style={{
                      width: "80px",
                      height: "4px",
                      backgroundColor: "#ae0a0b",
                      margin: "0 auto",
                      borderRadius: "2px"
                    }} />
                  </div>
                ) : (
                  /* Standard Horoscope Header */
                  <div className="p-3 p-md-4 text-center">
                    <div
                      style={{
                        width: isTarot ? "75px" : "80px",
                        height: isTarot ? "129px" : "80px",
                        borderRadius: isTarot ? "12px" : "50%",
                        boxShadow: isTarot ? "0 15px 35px rgba(0,0,0,0.15)" : "0 10px 30px rgba(0,0,0,0.08)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 20px",
                        overflow: "hidden",
                        position: "relative",
                        border: "none",
                      }}
                    >
                      {media && (
                        <img
                          src={`${assetbaseUrl?.value}${media}`}
                          alt={name}
                          style={{
                            width: "100%",
                            objectFit: isTarot ? "cover" : "cover",
                          }}
                        />
                      )}
                    </div>

                    <h2
                      style={{
                        fontSize: window.innerWidth < 768 ? "22px" : "26px",
                        fontWeight: "800",
                        color: "#2c3e50",
                        lineHeight: "1.3",
                      }}
                    >
                      {name_guj}{" "}
                      <span style={{ color: "#ddd", fontWeight: "300" }}>|</span>{" "}
                      {name}
                    </h2>

                    <h5>{letters ? letters : ""}</h5>

                    <div
                      style={{
                        width: "60px",
                        height: "4px",
                        background: "linear-gradient(to right, #ae0a0b, #ff7043)",
                        margin: "15px auto",
                        borderRadius: "2px",
                      }}
                    ></div>
                  </div>
                )}

                <div className="p-4 p-md-5">
                  {/* Actual Horoscope Text or No Data Message */}
                  {selectedData && !horoscopeError ? (
                    <>
                      <div
                        className="horoscope-details"
                        style={{
                          lineHeight: "2",
                          fontSize: window.innerWidth < 768 ? "17px" : "19px",
                          color: "#333",
                          textAlign: "justify",
                          position: "relative",
                          maxHeight: activePeriod === 'yearly' && !isExpanded ? "400px" : "none",
                          overflow: activePeriod === 'yearly' && !isExpanded ? "hidden" : "visible",
                          transition: "max-height 0.3s ease-in-out",
                          fontFamily: "'Noto Sans Gujarati', sans-serif"
                        }}
                      >
                        <div dangerouslySetInnerHTML={{ __html: detail }} />

                        {/* Styling for API injected content */}
                        <style>{`
                    .horoscope-details p { margin-bottom: 20px; }
                    .horoscope-details strong { color: #ae0a0b; }
                    @keyframes fadeIn {
                      from { opacity: 0; }
                      to { opacity: 1; }
                    }
                  `}</style>

                        {activePeriod === 'yearly' && !isExpanded && (
                          <div style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: "120px",
                            background: "linear-gradient(to bottom, transparent, white)",
                            pointerEvents: "none"
                          }} />
                        )}
                      </div>

                      {activePeriod === 'yearly' && !isExpanded && (
                        <div
                          className="d-flex align-items-center mt-3 mb-4"
                          onClick={() => {
                            setIsExpanded(true);

                            ReactGA.send({
                              hitType: "pageview",
                              page: `${window.location.pathname}`,
                              title: "Astrology",
                            });

                          }}
                          style={{ cursor: "pointer" }}
                        >
                          <div style={{ flex: 1, height: "1px", backgroundColor: "#ae0a0b" }}></div>
                          <div style={{
                            padding: "6px 25px",
                            border: "1px solid #ae0a0b",
                            borderRadius: "25px",
                            color: "#2c3e50",
                            fontWeight: '700',
                            margin: "0 15px",
                            backgroundColor: "#fff",
                            fontSize: window.innerWidth < 768 ? "14px" : "16px",
                            transition: "all 0.2s ease"
                          }}>
                            વધુ વાંચો
                          </div>
                          <div style={{ flex: 1, height: "1px", backgroundColor: "#ae0a0b" }}></div>
                        </div>
                      )}

                      <div className="mt-5 pt-4 text-center border-top">
                        {/* Placeholder for social sharing */}
                        <div
                          style={{
                            fontSize: "24px",
                            color: "#ae0a0b",
                            gap: "20px",
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <i
                            onClick={(e) => {
                              returnfalse("wa");
                              e.preventDefault();
                              const waUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(
                                waShareText
                              )}`;
                              const waWebUrl = `https://web.whatsapp.com/send?text=${encodeURIComponent(
                                waShareText
                              )}`;
                              const isMobile =
                                /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
                                  navigator.userAgent
                                );
                              window.open(isMobile ? waUrl : waWebUrl, "_blank");
                            }}
                            className="fab fa-whatsapp"
                            style={{ cursor: "pointer" }}
                          ></i>

                          <a
                            id="dropdown"
                            onClickCapture={handleFacebookShare}
                            onClick={() => handleShare("fb")}
                            aria-label="Facebook"
                            href={`${"https://facebook.com/sharer/sharer.php?display=page&picture=" +
                              encodeURIComponent(media) +
                              "&quote=" +
                              encodeURIComponent(title + urlshare) +
                              "&u=" +
                              encodeURIComponent(urlshare)
                              }`}
                            target="_blank"
                            rel="noreferrer"
                            className="fab fa-facebook"
                            style={{ cursor: "pointer", color: "#ae0a0b" }}
                          ></a>
                          <a
                            onClickCapture={() =>
                              ReactGA.event({
                                category: "Share",
                                action: "Twitter",
                                label: urlshare,
                              })
                            }
                            href={
                              "https://twitter.com/intent/tweet?url=" +
                              encodeURIComponent(urlshare)
                            }
                            target="_blank"
                            rel="noreferrer"
                            className="fab fa-twitter"
                            style={{ cursor: "pointer", color: "#ae0a0b" }}
                            aria-label="Twitter"
                          ></a>

                          <a
                            onClick={returnStop4}
                            target="_blank"
                            style={{ cursor: "pointer", color: "#ae0a0b" }}
                            href={`mailto:?subject=${title}&body=${title}%0D%0A%0D%0A${urlshare}`}
                            className="fa fa-envelope"
                          ></a>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div
                      className="text-center py-5"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "20px",
                      }}
                    >
                      <div style={{ fontSize: "60px", color: "#ccc" }}>
                        <i className="fas fa-exclamation-circle"></i>
                      </div>
                      <h3 style={{ color: "#2c3e50", fontWeight: "700" }}>
                        માહિતી મળી નથી
                      </h3>
                      <p style={{ color: "#95a5a6", fontSize: "16px" }}>
                        આ રાશિ માટે હાલમાં કોઈ માહિતી ઉપલબ્ધ નથી
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar Area */}
            <div className="col-lg-3 mt-4 mt-lg-0">
              <div
              // className="d-flex align-items-center justify-content-center"
              // style={{ minHeight: "300px", backgroundColor: "#f9f9f9" }}
              >
                <div className="text-center">
                  <div className="ad-side-head">
                    <div className="ad-side mb-2">
                      <div className="ad-section">
                        <InjectScript
                          script={
                            window.innerWidth < 991
                              ? adtwoHardcode166
                              : adtwoHardcode4
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
        <div className="video_ad"></div>
      </div>
    </>
  );
};

export default AstrologyDetailpage;
